import { Component, computed, inject, effect, Renderer2, ElementRef } from '@angular/core';
import { CookieConsentService } from '../../services/cookie-consent.service';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { SelectedWorkspaceStore } from 'src/app/stores/selected-workspace.store';
import { toSignal } from '@angular/core/rxjs-interop';
import { XButtonComponent } from '../../shared/x-kit/components/x-button/x-button.component';
import { filter, map, startWith } from 'rxjs/operators';

@Component({
  selector: 'app-cookie-consent-banner',
  standalone: true,
  imports: [CommonModule, TranslateModule, RouterModule, XButtonComponent],
  templateUrl: './cookie-consent-banner.component.html',
  styleUrls: ['./cookie-consent-banner.component.less'],
})
export class CookieConsentBannerComponent {
  private cookieService = inject(CookieConsentService);
  private workspaceStore = inject(SelectedWorkspaceStore);
  private router = inject(Router);
  private renderer = inject(Renderer2);
  private elementRef = inject(ElementRef);

  private user = toSignal(this.workspaceStore.user$);
  
  // Проверяем, находимся ли мы на странице авторизации
  isAuthPage = toSignal(
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => {
        const url = this.router.url;
        return url.startsWith('/auth') || url.includes('/register') || url.includes('/recovery');
      }),
      startWith(this.router.url.startsWith('/auth') || this.router.url.includes('/register') || this.router.url.includes('/recovery'))
    )
  );

  // Проверяем, находимся ли мы на странице terms-of-service или privacy-policy
  isLegalPage = toSignal(
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => {
        const url = this.router.url;
        return url.includes('/terms-of-service') || url.includes('/privacy-policy');
      }),
      startWith(this.router.url.includes('/terms-of-service') || this.router.url.includes('/privacy-policy'))
    )
  );

  visible = computed(() => {
    // Сначала проверяем localStorage - если есть согласие, не показываем
    if (this.cookieService.hasConsent()) {
      return false;
    }

    // Если принудительно показываем - показываем
    if (this.cookieService.forceShow()) {
      return true;
    }

    const currentUser = this.user();
    
    // Если пользователь не загружен или это гость - показываем баннер
    if (!currentUser || currentUser.id === 0) {
      return true;
    }
    
    // Для залогиненных пользователей проверяем профиль
    // Если согласие есть в профиле (значение "1" означает согласие) - не показываем
    if (currentUser.consent && currentUser.consent['ANALYTICS_COOKIES'] === '1') {
      return false;
    }

    // Показываем баннер
    return true;
  });

  onAccept() {
    this.cookieService.giveAnalyticsConsent();
  }

  onReject() {
    this.cookieService.rejectAnalyticsConsent();
  }

  constructor() {
    // Добавляем/удаляем класс к body когда баннер виден на страницах terms-of-service или privacy-policy
    effect(() => {
      const shouldAddPadding = this.isLegalPage() && this.visible();
      const body = document.body;
      
      if (shouldAddPadding) {
        this.renderer.addClass(body, 'cookie-banner-visible-on-legal-page');
      } else {
        this.renderer.removeClass(body, 'cookie-banner-visible-on-legal-page');
      }
    });
  }
} 